import React, { useState, useCallback, useMemo } from 'react'
import Grid from '@material-ui/core/Grid'
import Paper from '@material-ui/core/Paper'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import CircularProgress from '@material-ui/core/CircularProgress'
import RequiredText from '../../../components/default/form/RequiredText'
import LoadingAndErrorFrame from '../../../components/default/frames/LoadingAndErrorFrame'
import NumberInput from '../../../components/default/form/NumberInput'
import { useCommonStyles } from '../../../utils/admin/StyleHook'
import useT from '../../../i18ns/admin/useT'
import { Path } from '../../../components/admin/route/route'
import { useGetDataByPayload, useDialogHandleClick, useRedirectHandleBack } from '../../../utils/default/ComplexFlowHook'
import { usePageFlow } from '../../../utils/default/PageFlowHook'
import useGDK from '../../../providers/admin/gdk/useGDK'
import { GashGrant, GashUserInfo, GDKError } from '@golden/gdk-admin'
import DropDown from '../../../components/default/form/DropDown'
import useGlobalDialog from '../../../providers/admin/dialog/useGlobalDialog'
import { createGlobalDialogConfig } from '../../../utils/default/DialogHelper'
import { formatMoney } from '../../../utils/default/TableHelper'
import { getCashInputProps } from '../../../utils/default/FormHelper'

const initialForm: (account?: string) => ({
  account: string,
  transactionType: '-' | 'grant' | 'reclaim',
  gash: string,
  memo: string,
  code: string
}) = (account = '') => ({
  account,
  transactionType: '-',
  gash: '',
  memo: '',
  code: ''
})
const PlayerReportGashLogAddPage: React.FC = () => {
  const gdk = useGDK()
  const commonClasses = useCommonStyles()
  const { t } = useT()
  const globalDialog = useGlobalDialog()

  const [account, setAccount] = useState('')
  const [form, setForm] = useState(initialForm())
  const [result, setResult] = useState<GashUserInfo | null>(null)
  const options = useMemo(() => [
    {
      name: '-',
      value: '-'
    },
    {
      name: t('common.grant'),
      value: 'grant'
    },
    {
      name: t('common.reclaim'),
      value: 'reclaim'
    }
  ], [])

  const memoInputProps = useMemo(() => ({
    classes: { input: commonClasses.memoField },
    inputProps: {
      placeholder: t('placeholder.inputGashAddMemo'),
      maxLength: 30
    }
  }), [commonClasses.memoField, t])
  const amountInputProps = {
    ...getCashInputProps()
  }
  const pageFlow = usePageFlow()
  const payload = useMemo(() => ({ account: form.account }), [form.account])
    useGetDataByPayload({
      payload,
      gdkFunc: (payload) => gdk.history.getGashUserInfo(payload),
      gdkFuncDependencies: [gdk],
      onBeforeFetch: pageFlow.setLoadingStart,
      onSuccessDependencies: [payload, gdk],
      onSuccess: (res: GashUserInfo) => {
        setResult(res)
        pageFlow.setContentShow()
      },
      onError: (error: GDKError) => {
        pageFlow.setGDKError(error)
      },
      canLoadData: !!payload.account
    })
  const [loading, setLoading] = useState<boolean>(false)
  const disabled = useMemo(() => {
    if (form.transactionType === '-') return true
    if (Number(form.gash) === 0) return true
    if (form.memo === '') return true
    if (form.transactionType === 'grant') return form.code === ''
    return false
  }, [form])
  const request = useMemo(() => ({
    account: form.account,
    gash: Number(form.gash),
    memo: form.memo,
    ...form.transactionType === 'grant' ? { code: form.code } : {}
  }), [form])

const [handleDebouncedBack] = useRedirectHandleBack({
    path: Path.PLAYER_REPORT_GASH_LOG
  })
  const handleSubmit = useDialogHandleClick({
      dialogId: 'gashLogAdd',
      globalDialog,
      changeDialogConfig: createGlobalDialogConfig({
        showIcon: false,
        message: t('dialog.addGashLog', { account: form.account, type: form.transactionType === 'grant' ? t('common.grant') : t('common.reclaim'), gash: formatMoney (form.gash, 0) })
      }),
      successDialogConfig: createGlobalDialogConfig({
        showIcon: false,
        message: t('dialog.createSuccess'),
        showCancel: false
      }),
      getFailDialogConfig: (error: string) => createGlobalDialogConfig({
        variant: 'error',
        showIcon: true,
        message: error,
        showCancel: false
      }),
      payload: request,
      gdkFunc: (payload) => {
        setLoading(true)
        return form.transactionType === 'grant' ? gdk.history.postGashGrant(payload as GashGrant) : gdk.history.postGashReclaim(payload)
      },
      onError: (error) => {
        setLoading(false)
        globalDialog.setConfig(createGlobalDialogConfig({
          showIcon: true,
          variant: 'error',
          showCancel: false,
          message: error.message
        }))
        globalDialog.setOpen({
          id: 'gashLogAddFailed',
          value: true,
          isOK: false
        })
      },
      onSuccess: () => {
        setLoading(false)
      },
      gdkFuncDependencies: [gdk, form.transactionType],
      afterSuccessDialog: handleDebouncedBack,
    })

  return (
    <Box padding={5}>
      <Grid container direction="column" spacing={2}>
        <Grid item>
          <Paper>
            <Box padding={4}>
              <Grid container direction="column" spacing={2}>
                <Grid item>
                  <Box
                    paddingY={1.25}
                    paddingX={2}
                    className={commonClasses.pinkTitleBar}
                  >
                    <Grid container justifyContent="space-between" alignItems="center">
                      <Grid>
                        <Typography variant="h5">
                          {t('common.playerSearch')}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
                <Grid item>
              <Grid container direction="row" spacing={2} justifyContent="space-between">
                <Grid item xs={12} md={6} lg={3}>
                  <TextField
                    label={t('common.playerAccount')}
                    placeholder={t('placeholder.inputPlayerAccount')}
                    value={account}
                    onChange={useCallback((event) => { setAccount(event.target.value) }, [])}
                    fullWidth
                    required
                  />
                </Grid>
              </Grid>
            </Grid>
                <Grid item>
                  <RequiredText />
                </Grid>
                <Grid item>
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      className={commonClasses.purpleGradualButton}
                      onClick={useCallback(() => {
                        setForm(initialForm(account))
                      }, [account])}
                      disabled={account === ''}
                    >
                      {t('common.search')}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>
        {
          form.account && (
            <>
              <Grid item>
                <Paper>
                  <Box padding={5}>
                    <LoadingAndErrorFrame {...pageFlow.status}>
                      <Grid container direction="column" spacing={2}>
                        <Grid item>
                          <Box
                            paddingY={1.25}
                            paddingX={2}
                            className={commonClasses.pinkTitleBar}
                          >
                            <Typography variant="h5">
                              {t('common.playerInformation')}
                            </Typography>
                          </Box>
                        </Grid>
                        {result
                          ? (
                            <React.Fragment>
                              <Grid item>
                                <Grid container direction="row" spacing={2}>
                                  <Grid item xs={12} md={3}>
                                    <TextField
                                      label={t('common.playerAccount')}
                                      value={result.account}
                                      disabled
                                      fullWidth
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={3}>
                                    <TextField
                                      label={t('common.openimId')}
                                      value={result?.openim_id ?? t('common.notBind')}
                                      disabled
                                      fullWidth
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={3}>
                                    <TextField
                                      label={t('common.gashBalance')}
                                      value={result.gash_amount}
                                      disabled
                                      fullWidth
                                    />
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item>
                                <Grid container direction="row" spacing={2}>
                                  <Grid item xs={12} md={3}>
                                    <DropDown
                                      label={t('common.transactionType')}
                                      value={form.transactionType}
                                      options={options}
                                      fullWidth
                                      required
                                      onChange={(event) => {
                                        const value = event.target.value as '-' | 'grant' | 'reclaim'
                                        setForm((form) => ({ ...form, code: '', transactionType: value }))
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={3}>
                                    <NumberInput
                                      label={t('common.gashPoint')}
                                      value={form.gash}
                                      fullWidth
                                      required
                                      inputProps={amountInputProps}
                                      onChange={(event) => {
                                        const value = event.target.value
                                        setForm((form) => ({ ...form, gash: value }))
                                      }}
                                    />
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item>
                                <Grid container direction="row" spacing={2}>
                                  <Grid item xs={12} md={6}>
                                    <TextField
                                      label={t('common.memo')}
                                      placeholder={t('placeholder.inputGashAddMemo')}
                                      value={form.memo}
                                      fullWidth
                                      required
                                      multiline
                                      type="text"
                                      margin="normal"
                                      variant="outlined"
                                      InputProps={memoInputProps}
                                      onChange={(event) => {
                                        const value = event.target.value
                                        setForm((form) => ({ ...form, memo: value }))
                                      }}
                                    />
                                  </Grid>
                                </Grid>
                              </Grid>
                              {
                                form.transactionType === 'grant' && (
                                  <Grid item>
                                    <Grid container direction="row" spacing={2}>
                                      <Grid item xs={12} md={3}>
                                        <TextField
                                          label={t('common.googleCode')}
                                          value={form.code}
                                          fullWidth
                                          required
                                          onChange={(event) => {
                                            const value = event.target.value
                                            setForm((form) => ({ ...form, code: value }))
                                          }}
                                        />
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                )
                              }
                              <Grid item>
                                <RequiredText />
                              </Grid>
                            </React.Fragment>
                          )
                          : (
                            <Box display="flex" justifyContent="center" padding={1} overflow="auto">
                              <Typography variant="h3">
                                {t('common.noData')}
                              </Typography>
                            </Box>
                          )}
                      </Grid>
                    </LoadingAndErrorFrame>
                  </Box>
                </Paper>
              </Grid>
              <Box paddingTop={2}>
                <Grid container direction="row" justifyContent="flex-end" spacing={2}>
                  <Grid item>
                    <Button
                      onClick={handleDebouncedBack}
                      className={commonClasses.greyButton}
                    >
                      {t('common.cancel')}
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      type="submit"
                      className={commonClasses.purpleGradualButton}
                      disabled={loading || disabled}
                      onClick={handleSubmit}
                    >
                      {loading
                        ? (
                          <Box display="flex" alignItems="center" justifyContent="center">
                            <CircularProgress size={24} />
                          </Box>
                        )
                        : t('common.confirmCreate')}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </>
          )
        }
      </Grid>
    </Box>
  )
}

export default React.memo(PlayerReportGashLogAddPage)
